import { Team, ClubKits } from './team';
import { CompetitionType } from './competition';
import type { LiveFeedPhase, MatchStatus } from './matchStatus';
import type { LiveFeedEvent } from './liveFeed';
import type { PlayerPosition } from './player';

export type HomeAndAway = {
  home: string;
  away: string;
};

export type TeamKitColors = {
  kits?: ClubKits | null;
  homeKitOutfieldShirtColor?: string | null;
  homeKitShortsColor?: string | null;
  homeKitSocksColor?: string | null;
};

export type Match = {
  id: string;
  isInternal: boolean;
  externalId?: string;
  currentMinute?: string;
  // 2024-03-29
  date: string;
  // 15:00:00
  time?: string;
  attendance: number;
  status: MatchStatus;
  // Resolved for display: a non-blank `venue`, else the home club's stadiumName, else 'N/A'.
  stadium?: string;
  // The raw free-text venue stored on the match: set by the admin match form on an internal match,
  // and by IFAS updates and the bulk import. What the admin form edits — never `stadium`.
  venue?: string;
  referee?: string;
  // Set when IFAS awarded the match to one side because the other did not play it, e.g. "Awarded Home Win".
  // `null` once IFAS says it is not awarded; absent when no push has said anything about it.
  awardedStatus?: string | null;
  score: {
    current: {
      away: number;
      home: number;
    };
    // `null` is only ever seen on a socket `MatchUpdate` payload, where it means "the
    // half-time score was cleared". Omitting the key there would be indistinguishable
    // from "unchanged", because the client deep-merges `score` (see mergeMatchUpdate).
    halfTime?: {
      away: number;
      home: number;
    } | null;
  };
  penaltyScores?: {
    home: number;
    away: number;
  };
  goals?: {
    home: Goal[];
    away: Goal[];
  };
  competition: {
    id: string;
    name: string;
    shortName?: string;
    slug?: string;
    type?: CompetitionType;
  };
  /**
   * The group of the competition's stage this fixture belongs to, for a competition whose stage an
   * editor grouped (SC-1227). Absent for every competition that keeps a single table — which is
   * every league — and absent for a cross-group knockout tie, which belongs to no group's standings.
   *
   * Carried so a consumer can tell which of a cup's tables a fixture counts towards without
   * re-deriving it from stage membership. The Match Centre rail needs exactly that: a day's cup
   * fixtures can span several groups, and without this it cannot know which tables to show.
   */
  group?: {
    id: string;
    name: string;
  };
  round?: {
    id: string;
    name: string;
  };
  subs?: {
    home: PlayerLineup[];
    away: PlayerLineup[];
  };
  lineups?: {
    home: PlayerLineup[];
    away: PlayerLineup[];
  };
  homeTeam: Omit<Team, 'createdBy' | 'updatedBy' | 'createdAt' | 'updatedAt' | 'teams' | 'gender' | 'club'> & {
    club?: Omit<Team['club'], 'createdBy' | 'updatedBy' | 'createdAt' | 'updatedAt'>;
  } & { kitColors?: TeamKitColors | null; manager?: string | null };
  awayTeam: Omit<Team, 'createdBy' | 'updatedBy' | 'createdAt' | 'updatedAt' | 'teams' | 'gender' | 'club'> & {
    club?: Omit<Team['club'], 'createdBy' | 'updatedBy' | 'createdAt' | 'updatedAt'>;
  } & { kitColors?: TeamKitColors | null; manager?: string | null };
  info?: string;

  liveCommentaryHome?: string;
  liveCommentaryAway?: string;
  liveStreamPreGameHome?: string;
  liveStreamPreGameAway?: string;
  liveStreamHome?: string;
  liveStreamAway?: string;
  matchProgrammeHome?: string;
  matchProgrammeAway?: string;
  matchHighlightsHome?: string;
  matchHighlightsAway?: string;
  podcastHome?: string;
  podcastAway?: string;
  ticketsHome?: string;
  ticketsAway?: string;
  recommendedHotel?: string;
  preGameInterviewHome?: string;
  preGameInterviewAway?: string;
  // Present only when fetched with ?withEvents=1 (see fetchMatchById's withEvents
  // option / matches.service.ts getMatch) — the full live-feed event log used to
  // build the Match Detail Timeline tab (buildTimeline.ts).
  events?: LiveFeedEvent[];
};

/**
 * The match fields only a CMS editor writes — never a data provider. An IFAS pull or push carries
 * no opinion about a fixture's ticket links or its "info" blurb, so a provider path that writes one
 * can only ever be clearing what an editor typed.
 *
 * This list is the single declaration of that ownership. dataPuller's Match schema omits every
 * field on it (its `match-field-ownership.spec.ts` pins that), so Mongoose casts them out of every
 * update that process issues; its mappers are typed so emitting one is a compile error; and the
 * API's push path is pinned never to write one. Typed against `Match` so it cannot name a field
 * that does not exist. Adding an editor-only field to the match form means adding it here.
 */
export const MATCH_EDITOR_OWNED_FIELDS = [
  'info',
  'recommendedHotel',
  'ticketsHome',
  'ticketsAway',
  'podcastHome',
  'podcastAway',
  'matchProgrammeHome',
  'matchProgrammeAway',
  'matchHighlightsHome',
  'matchHighlightsAway',
  'liveStreamPreGameHome',
  'liveStreamPreGameAway',
  'liveStreamHome',
  'liveStreamAway',
  'preGameInterviewHome',
  'preGameInterviewAway',
  'liveCommentaryHome',
  'liveCommentaryAway',
] as const satisfies readonly (keyof Match)[];
export type MatchEditorOwnedField = (typeof MATCH_EDITOR_OWNED_FIELDS)[number];

export const MatchPeriodFilter = {
  Days: 'Days',
  Weeks: 'Weeks',
  Months: 'Months',
  Custom: 'Custom',
} as const;
export type MatchPeriodFilter = (typeof MatchPeriodFilter)[keyof typeof MatchPeriodFilter];

export const MatchSort = {
  ASC: 'asc',
  DESC: 'desc',
} as const;
export type MatchSort = (typeof MatchSort)[keyof typeof MatchSort];

export { MatchStatus } from './matchStatus';

type Player = {
  id: string;
  name: string;
  // Master football_players id — present only once this player is reconciled in
  // master (additive, backend-populated). Absent means there's no player-profile
  // page to link to yet; consumers must render plain text in that case, never a
  // dead link. Populated on lineup/sub/goal players wherever `Player` is nested.
  profileId?: string;
  // The role this player filled in THIS match, falling back to the career position on their
  // master profile when the match names none. IFAS identifies only the goalkeeper, and only by
  // teamsheet order, so in practice this is `'GK'` on one starter per side and `null` on the
  // rest. `null` means unknown, never "outfield" — don't render an absence as a claim.
  position?: PlayerPosition | null;
};

type Goal = {
  player: Player;
  minute: number;
  phase?: LiveFeedPhase;
  description?: string;
  /**
   * How the goal was scored. Carried through by `mapLineup` (SC-718) so a view can render the
   * penalty / own-goal glyph from the stored value instead of guessing. Optional because goals
   * imported before `Goal.goalType` existed have none — treat an absent value as a normal goal.
   */
  goalType?: GoalTypeInterface;
  sort: number;
};

export type PlayerLineup = {
  shirtNumber: number;
  sort: number;
  player: Player;
  isCaptain: boolean;
  // In minutes indicating when the card was shown
  yellowCards?: number[];
  // In minutes indicating when the card was shown
  redCard?: number;
  replaces?: {
    player: Player;
    minute: number;
    shirt: number;
  };
  replacedBy?: {
    player: Player;
    minute: number;
    shirt: number;
  };
  goals?: Goal[];
};

export const MatchStatusFilter = {
  LIVE: 'live',
  POSTPONED: 'postponed',
  RESULT: 'result',
  FIXTURE: 'fixture',
  ANY: 'any',
  ABANDONED: 'abandoned',
} as const;
export type MatchStatusFilter = (typeof MatchStatusFilter)[keyof typeof MatchStatusFilter];

/**
 * The five user-selectable status categories, in the order the admin filter lists them.
 * `MatchStatusFilter.ANY` is excluded: it is a no-filter sentinel, byte-identical to omitting
 * `status` altogether, and the UI expresses that state by selecting nothing.
 */
export const SELECTABLE_MATCH_STATUS_FILTERS = [
  MatchStatusFilter.LIVE,
  MatchStatusFilter.FIXTURE,
  MatchStatusFilter.RESULT,
  MatchStatusFilter.POSTPONED,
  MatchStatusFilter.ABANDONED,
] as const;

/**
 * Query sentinel meaning "no season constraint". Explicit rather than an omitted param: an absent
 * `season` means "apply the default current season", so the two states must stay distinguishable
 * for a bookmarked all-seasons view to reopen as itself.
 */
export const ALL_SEASONS = 'all';

/**
 * URL sentinel meaning "no date bound", written by clearing the admin Matches date picker.
 * An absent `dates` token still means "pre-fill today", which is why this cannot be an omission.
 */
export const ALL_DATES = 'all';

/**
 * One season the admin Matches filter can offer, as returned by `GET /matches/seasons`.
 *
 * Seasons are per-competition in the DB, so an entry is the merge of every config the tenant owns
 * under that normalised name: `startDate`/`endDate` are the min/max envelope across them and
 * `isCurrent` is true when any one of them is flagged current. Dates are ISO strings over the wire.
 */
export type MatchSeasonOption = {
  name: string;
  isCurrent: boolean;
  startDate: string;
  endDate: string;
};

export type MatchFilterConfig = {
  label: string;
  competitions: string;
};

export const GoalType = {
  Normal: 'normal',
  Penalty: 'penalty',
  OwnGoal: 'own-goal',
  PenaltyShootout: 'penalty-shootout',
} as const;
export type GoalTypeInterface = (typeof GoalType)[keyof typeof GoalType];

export const CardType = {
  FirstYellow: 'first_yellow_card',
  SecondYellow: 'second_yellow_card',
  Red: 'red_card',
} as const;
export type CardTypeInterface = (typeof CardType)[keyof typeof CardType];

export type MatchFilter = {
  competition?: string;
  teamId?: string;
  // @deprecated use teamId instead
  club?: string;
  status?: MatchStatusFilter | string;
  period?: MatchPeriodFilter;
  periodLimit?: number;
  startDate?: string;
  endDate?: string;
  limit?: number;
  page?: number;
  sort?: MatchSort;
};

/**
 * One observation of the score as IFAS reported it, appended to `matches.scoreHistory`
 * whenever a `/fixture/{id}` poll returns a score different from the last entry.
 *
 * `minute` is the match minute at the moment of observation, parsed to whole minutes from
 * `getCurrentMinute`'s "MM:SS" string — `null` before kick-off, when that function returns
 * null. It is OUR wall-clock elapsed time, not IFAS's event clock, which is why consumers
 * match against it with a tolerance window rather than an exact minute.
 */
export type MatchScoreSample = {
  homeScore: number;
  awayScore: number;
  minute: number | null;
  observedAt: Date | string;
  matchPhase: MatchStatus;
};
